import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { finalize, Subscription } from 'rxjs';
import { EmployeesService } from '../../employees.service';
import { NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Action, PaginatedResponse } from '../../../common/models/common.model';
import { Designation, Employee } from '../../employees.model';
import { buildFilterQuery, getDefaultProfilePicUrl, handleImageError, SnackbarAlertStatus } from '../../../utils';
import { SnackbarService } from '../../../services/snackbar.service';
import { DesignationService } from '../../designation.service';

@Component({
  selector: 'app-employees',
  standalone: false,
  templateUrl: './employees.component.html',
  styleUrl: './employees.component.scss',
})
export class EmployeesComponent implements OnInit, OnDestroy {
  @ViewChild('addNewEmployeeContent') addNewEmployeeContent!: TemplateRef<any>;
  @ViewChild('deleteEmployeeContent') deleteEmployeeContent!: TemplateRef<any>;

  protected isEmployeesDataLoading: boolean = false;
  protected isEmployeeDeleting: boolean = false;
  protected isExporting: boolean = false;
  protected employees: Employee[] = [];
  protected modalRef!: NgbModalRef;
  protected employeeToBeDelete: string | null = null;
  protected employeeToBeEdit: Employee | null = null;
  protected hasNextPage = false;
  protected hasPreviousPage = false;
  protected totalCount: number = 0;
  protected searchTerm: string = '';

  protected designations: Designation[] = [];
  protected selectedDesignationId: string | null = null;

  /** Employee ID sort direction. The API orders by employee_number, not the UUID pk. */
  protected sortDirection: 'asc' | 'desc' = 'asc';

  protected currentPage: number = 1;

  private _employeesSubscription!: Subscription;
  private _designationSubscription!: Subscription;
  private searchDebounceTimer!: any;

  protected readonly Math = Math;
  protected readonly Action = Action;

  constructor(
    private _employeesService: EmployeesService,
    private _modalService: NgbModal,
    private _snackbarService: SnackbarService,
    private _designationService: DesignationService
  ) {}

  ngOnInit(): void {
    this._getEmployeesList();
    this._getDesignationList();
  }

  // Getter functions

  protected editEmployee(emp: Employee): void {
    this.employeeToBeEdit = emp;

    const modalOptions: NgbModalOptions = {
      backdrop: 'static',
      size: 'xl',
    };

    this._openModal(this.addNewEmployeeContent, modalOptions);
  }

  protected onClickDeleteEmployee(empId: string): void {
    this.employeeToBeDelete = empId;

    const modalOptions: NgbModalOptions = {
      backdrop: 'static',
      size: 'md',
    };

    this._openModal(this.deleteEmployeeContent, modalOptions);
  }

  protected onAddNewEmployee(): void {
    this.employeeToBeEdit = null;

    const modalOptions: NgbModalOptions = {
      backdrop: 'static',
      size: 'xl',
    };

    this._openModal(this.addNewEmployeeContent, modalOptions);
  }

  protected onDeleteEmployee(): void {
    if (!this.employeeToBeDelete) {
      return;
    }

    this.isEmployeeDeleting = true;

    this._employeesSubscription = this._employeesService
      .delete(this.employeeToBeDelete)
      .pipe(
        finalize(() => {
          this.employeeToBeDelete = null;
          this.isEmployeeDeleting = false;
        })
      )
      .subscribe({
        complete: () => {
          this.employees = this.employees.filter(emp => emp.id !== this.employeeToBeDelete);
          this.totalCount -= 1;
          this.modalRef.close();
        },
      });
  }

  protected onSaveEmployeeInfo(employeeInfo: Employee): void {
    if (this.employeeToBeEdit) {
      const index = this.employees.findIndex(emp => emp.id === this.employeeToBeEdit?.id);

      if (index !== -1) {
        // Update the existing employee info
        this.employees[index] = { ...this.employees[index], ...employeeInfo };
      }
    } else {
      // Add new employee
      this.employees.push(employeeInfo);
      this.totalCount += 1;
    }

    this.modalRef?.dismiss();
  }

  protected onExportEmployees(): void {
    this.isExporting = true;

    this._employeesService
      .export()
      .pipe(finalize(() => (this.isExporting = false)))
      .subscribe({
        next: (blob: Blob) => {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = 'employees.xlsx';
          a.click();
          URL.revokeObjectURL(url);
          this._snackbarService.show('Employee list exported successfully', SnackbarAlertStatus.SUCCESS);
        },
      });
  }

  protected onPageChange(page: number): void {
    this.currentPage = page;
    this._getEmployeesList();
  }

  protected onToggleEmployeeIdSort(): void {
    this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    this.currentPage = 1;
    this._getEmployeesList();
  }

  protected onDesignationFilterChange(): void {
    this.currentPage = 1;
    this._getEmployeesList();
  }

  protected onSearchKeyUp(): void {
    // Clear any existing timer
    clearTimeout(this.searchDebounceTimer);

    // Set a new debounce timer (500 ms)
    this.searchDebounceTimer = setTimeout(() => {
      this.currentPage = 1;
      this._getEmployeesList();
    }, 500);
  }

  // Private methods

  private _getEmployeesList(): void {
    this.isEmployeesDataLoading = true;

    const reqParams = buildFilterQuery({
      page: this.currentPage,
      search: this.searchTerm,
      designation: this.selectedDesignationId,
      ordering: this.sortDirection === 'asc' ? 'employee_number' : '-employee_number',
    });

    this._employeesSubscription = this._employeesService
      .list(reqParams)
      .pipe(
        finalize(() => {
          this.isEmployeesDataLoading = false;
        })
      )
      .subscribe({
        next: (employeesList: PaginatedResponse<Employee>) => {
          this.employees = employeesList.results || [];
          this.hasNextPage = !!employeesList.next;
          this.hasPreviousPage = !!employeesList.previous;
          this.totalCount = employeesList.count;
        },
      });
  }

  private _getDesignationList(): void {
    this._designationSubscription = this._designationService.list().subscribe({
      next: (designations: Designation[]) => {
        this.designations = designations;
      },
      error: err => {
        console.error(err);
      },
    });
  }

  private _openModal(content: TemplateRef<any>, options: NgbModalOptions): void {
    this.modalRef = this._modalService.open(content, options);
  }

  ngOnDestroy(): void {
    this._employeesSubscription?.unsubscribe();
    this._designationSubscription?.unsubscribe();
    this.modalRef?.dismiss();
  }

  protected readonly getDefaultProfilePicUrl = getDefaultProfilePicUrl;
  protected readonly handleImageError = handleImageError;
}
